<template>
  <div 
    ref="previewRef"
    v-if="isVisible" 
    class="appendix-preview"
    :style="{ 
      left: position.x + 'px', 
      top: position.y + 'px' 
    }"
  >
    <div class="preview-header">
      <h4 class="squids-font">{{ previewTitle }}</h4>
      <button @click="closePreview" class="close-btn">&times;</button>
    </div>
    <div class="preview-content scifi-font-sml">
      <div v-if="previewContent" v-html="previewContent"></div>
      <div v-else-if="loading" class="loading">Loading...</div>
      <div v-else class="error">Content not found</div>
    </div>
  </div>
</template>

<script setup>
import { ref, onMounted, onUnmounted } from 'vue'
import { watch, nextTick } from 'vue'

// Query the appendix content using the correct path
let appendixData = null
try {
  const { data: appendixContent } = await useAsyncData('appendix-preview', () => queryCollection('content').path('/galactic-guide-to-the-universe/appendix/appendix').first());
  appendixData = appendixContent.value
} catch (error) {
  console.error('Failed to load appendix data:', error)
}

const isVisible = ref(false)
const loading = ref(false)
const previewContent = ref('')
const previewTitle = ref('')
const position = ref({ x: 0, y: 0 })
const previewRef = ref(null)
const isHoveringPopup = ref(false)

let currentTarget = null
let hoverTimeout = null
let inactivityTimeout = null


// Add event listeners for link hover detection on mount
onMounted( async() => {
  // Load appendix data when component mounts
  document.addEventListener('mouseover', handleLinkHover)
  document.addEventListener('mouseout', handleLinkLeave)
  document.addEventListener('click', handleLinkClick)
  document.addEventListener('click', handleGlobalClick) 

  // Add hover tracking to the popup
  previewRef.value?.addEventListener('mouseenter', () => {
    console.log('[Preview] mouseenter on popup')
    isHoveringPopup.value = true
    clearInactivityTimeout()
  })

  previewRef.value?.addEventListener('mouseleave', () => {
    console.log('[Preview] mouseleave on popup')
    isHoveringPopup.value = false
    startInactivityTimeout()
  })
})

watch(isVisible, async (visible) => {
  if (!visible) return

  await nextTick() // wait until DOM is rendered
  const el = previewRef.value
  if (!el) return

  console.log('[Watch] Popup became visible. Attaching mouseenter/leave')

  el.addEventListener('mouseenter', onPopupMouseEnter)
  el.addEventListener('mouseleave', onPopupMouseLeave)
})


function onPopupMouseEnter() {
  console.log('[Preview] mouseenter on popup')
  isHoveringPopup.value = true
  clearInactivityTimeout()
}

function onPopupMouseLeave() {
  console.log('[Preview] mouseleave on popup')
  isHoveringPopup.value = false
  startInactivityTimeout()
}

onUnmounted(() => {
  document.removeEventListener('mouseover', handleLinkHover)
  document.removeEventListener('mouseout', handleLinkLeave)
  document.removeEventListener('click', handleLinkClick)
  document.removeEventListener('click', handleGlobalClick)
  
  const el = previewRef.value
  if (el) {
    el.removeEventListener('mouseenter', onPopupMouseEnter)
    el.removeEventListener('mouseleave', onPopupMouseLeave)
  }

  if (hoverTimeout) clearTimeout(hoverTimeout)
  clearInactivityTimeout()
})

function startInactivityTimeout() {
  console.log('[Inactivity] Timeout starting...')
  clearInactivityTimeout()
  inactivityTimeout = setTimeout(() => {
    console.log('[Inactivity] Timeout triggered — closing popup')
    closePreview()
  }, 2000)
}

function clearInactivityTimeout() {
  if (inactivityTimeout) {
    console.log('[Inactivity] Timeout cleared')
    clearTimeout(inactivityTimeout)
    inactivityTimeout = null
  }
}

const showPreview = async (event, href) => {
  const originalPath = window.location.pathname + window.location.hash
  console.log('[Link Hover] Show preview requested for:', href)

  // Clear any existing timeout
  if (hoverTimeout) {
    clearTimeout(hoverTimeout)
  }

  // Set a delay before showing preview
  hoverTimeout = setTimeout(async () => {
    console.log('[Link Hover] Showing preview for:', href)
    currentTarget = event.target
    
    if (window.location.pathname + window.location.hash !== originalPath) {
      console.log('[Link Hover] Aborted preview due to navigation')
      return
    }
    // Position the preview near the cursor
    const rect = event.target.getBoundingClientRect()
    position.value = {
      x: Math.min(rect.left + window.scrollX, window.innerWidth - 400),
      y: rect.bottom + window.scrollY + 5
    }

    // Extract the anchor from the href
    const anchor = href.split('#')[1]
    if (!anchor) return

    loading.value = true
    isVisible.value = true
    previewTitle.value = anchor.charAt(0).toUpperCase() + anchor.slice(1).replace(/-/g, ' ')

    try {
      // Find the section content from cached appendix data
      const sectionContent = findSectionContent(anchor)
      if (sectionContent) {
        previewContent.value = sectionContent
      } else {
        previewContent.value = `<p>Section "${anchor}" not found in appendix.</p>`
      }
    } catch (error) {
      console.error('Error finding section content:', error)
      previewContent.value = `<p>Error loading content for "${anchor}".</p>`
    } finally {
      loading.value = false
    }
  }, 500) // 500ms delay
}

const closePreview = () => {
  console.log('[Close] Closing preview')
  isVisible.value = false
  previewContent.value = ''
  currentTarget = null
  clearInactivityTimeout()
}

function extractSectionFromAst(ast, anchor, headingTag = 'h2') {
  if (!Array.isArray(ast)) return []

  const searchId = anchor.toLowerCase()
  let foundIndex = -1

  // Find the heading element with matching id
  for (let i = 0; i < ast.length; i++) {
    const node = ast[i]
    if (
      node.type === 'element' &&
      node.tag === headingTag &&
      node.props?.id?.toLowerCase() === searchId
    ) {
      foundIndex = i
      break
    }
  }

  if (foundIndex === -1) return []

  const sectionNodes = []
  for (let i = foundIndex + 1; i < ast.length; i++) {
    const node = ast[i]
    if (node.type === 'element' && node.tag === headingTag) {
      break
    }
    sectionNodes.push(node)
  }

  return sectionNodes
}

function renderAstToHtml(nodes) {
  return nodes.map(renderNode).join('\n')
}

function renderNode(node) {
  if (node.type === 'text') {
    return escapeHtml(node.value || '')
  }

  if (node.type === 'element') {
    const tag = node.tag
    const attrs = renderAttributes(node.props || {})
    const children = (node.children || []).map(renderNode).join('')
    return `<${tag}${attrs}>${children}</${tag}>`
  }

  // fallback for unknown node types
  return ''
}

function renderAttributes(props) {
  if (!props) return ''
  return Object.entries(props)
    .map(([key, value]) => ` ${key}="${escapeHtml(value)}"`)
    .join('')
}

function escapeHtml(str) {
  return String(str)
    .replace(/&/g, '&amp;')
    .replace(/"/g, '&quot;')
    .replace(/'/g, '&#39;')
    .replace(/</g, '&lt;')
    .replace(/>/g, '&gt;')
}

const findSectionContent = (anchor) => {
  if (!appendixData || !Array.isArray(appendixData.body?.children)) return null
  const nodes = extractSectionFromAst(appendixData.body.children, anchor)
  if (!nodes.length) return null
  return renderAstToHtml(nodes)
}

const hidePreview = () => {
  console.log('[Link Leave] hidePreview called')
  if (hoverTimeout) {
    console.log('[Timeout] clearing hoverTimeout')
    clearTimeout(hoverTimeout)
    hoverTimeout = null
  }

  setTimeout(() => {
    const previewElement = previewRef.value

    const stillHovering = previewElement?.matches(':hover') || currentTarget?.matches(':hover')
    console.log(`[Hover Check] stillHovering=${stillHovering}`)

    if (stillHovering) {
      if (inactivityTimeout) {
        clearTimeout(inactivityTimeout)
        inactivityTimeout = null
      }
      return
    }

    console.log('[Inactivity] starting 2s close timeout')
    startInactivityTimeout()
  }, 100)

}

const handleLinkHover = (event) => {
  const target = event.target
  if (target.tagName === 'A' && target.getAttribute('href')?.includes('/appendix/') && target.getAttribute('href')?.includes('#')) {
    console.log('[Event] mouseover on link:', target.getAttribute('href'))
    showPreview(event, target.getAttribute('href'))
  }
}

const handleLinkLeave = (event) => {
  const target = event.target
  if (target.tagName === 'A' && target.getAttribute('href')?.includes('/appendix/') && target.getAttribute('href')?.includes('#')) {
    console.log('[Event] mouseout from link:', target.getAttribute('href'))
    hidePreview()
  }
}

function handleGlobalClick(event) {
  const el = previewRef.value
  const clickedInside = el?.contains(event.target)
  console.log('[Event] global click detected:', clickedInside)

  if (!clickedInside) {
    closePreview()
  }
}

const handleLinkClick = (event) => {
  const target = event.target
  if (
    target.tagName === 'A' &&
    target.getAttribute('href')?.includes('/appendix/') &&
    target.getAttribute('href')?.includes('#')
  ) {
    console.log('[Event] link clicked:', target.getAttribute('href'))

    // Cancel pending preview
    if (hoverTimeout) {
      clearTimeout(hoverTimeout)
      hoverTimeout = null
    }

    closePreview()
  }
}

</script>

<style scoped>

.appendix-preview {
  position: absolute;
  z-index: 1000;
  width: 45vw; /* 45% of the viewport width */
  max-width: 90vw;
  background: #1a1a1a;
  border: 2px solid #00ff00;
  border-radius: 8px;
  box-shadow: 0 0 15px rgba(0, 255, 0, 0.3);
  font-family: 'Orbitron', sans-serif;
  color: #00ff00;
  overflow: hidden;
}

.preview-header {
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 10px 15px;
  background: rgba(0, 255, 0, 0.1);
  border-bottom: 1px solid #00ff00;
}

.preview-header h4 {
  margin: 0;
  font-size: var(--fs-display);
}

.close-btn {
  background: none;
  border: none;
  color: #00ff00;
  font-size: var(--fs-large);
  cursor: pointer;
  padding: 0;
  width: 20px;
  height: 20px;
  display: flex;
  align-items: center;
  justify-content: center;
}

.close-btn:hover {
  color: #ffffff;
}

.preview-content {
  padding: 12px;
  max-height: 300px;
  overflow-y: auto;
  font-family: 'Orbitron', sans-serif;
  font-size: var(--fs-base);
  letter-spacing: 2px;
  text-shadow: 1px 1px 2px #000;
}

.loading, .error {
  text-align: center;
  padding: 20px;
  color: #888;
}

.error {
  color: #ff6b6b;
}

/* Style the content within the preview */
.preview-content :deep(h1),
.preview-content :deep(h2),
.preview-content :deep(h3),
.preview-content :deep(h4),
.preview-content :deep(h5),
.preview-content :deep(h6) {
  color: #ffffff;
  margin-top: 0;
  margin-bottom: 10px;
  font-family: 'Orbitron', sans-serif;
  font-size: var(--fs-base);
  letter-spacing: 2px;
  text-shadow: 1px 1px 2px #000;
}

.preview-content :deep(p) {
  margin-bottom: 10px;
  font-family: 'Orbitron', sans-serif;
  font-size: var(--fs-base);
  letter-spacing: 2px;
  text-shadow: 1px 1px 2px #000;
}

.preview-content :deep(ul),
.preview-content :deep(ol) {
  margin-bottom: 10px;
  padding-left: 20px;
  font-family: 'Orbitron', sans-serif;
  font-size: var(--fs-base);
  letter-spacing: 2px;
  text-shadow: 1px 1px 2px #000;
}

.preview-content :deep(li) {
  margin-bottom: 5px;
  font-family: 'Orbitron', sans-serif;
  font-size: var(--fs-small);
  letter-spacing: 2px;
  text-shadow: 1px 1px 2px #000;
}

.preview-content :deep(strong) {
  color: #ffffff;
}

.preview-content :deep(em) {
  color: #cccccc;
}
</style>
